import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  roleChecked: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  roleChecked: false,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const checkedUidRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const checkAdmin = async (uid: string | undefined) => {
      // Skip re-checking (and re-gating) when the same user is still signed in,
      // e.g. on background token refreshes.
      if (checkedUidRef.current === (uid ?? null)) {
        setRoleChecked(true);
        return;
      }
      checkedUidRef.current = uid ?? null;
      if (!uid) {
        setIsAdmin(false);
        setRoleChecked(true);
        return;
      }
      setRoleChecked(false);
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      if (checkedUidRef.current !== uid) return;
      setIsAdmin(!!data);
      setRoleChecked(true);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        // Defer to avoid deadlock in the auth callback
        setTimeout(() => checkAdmin(session?.user?.id), 0);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      checkAdmin(session?.user?.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    checkedUidRef.current = null;
    setIsAdmin(false);
    setRoleChecked(true);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, roleChecked, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
